import { IService, Service } from '../core/service';
import { t_locator } from'../../../pip/library/pi-ip/pi-ip.js';
import { TResourceConfiguration } from '../core/resource';

/**
 * a process goes through a simple life-cycle, outlined on the main page.
 */
export type t_process_state =
  | 'import'
  | 'uninitialize'
  | 'initialize'
  | 'deactivate'
  | 'activate'
  | 'unimport'
  | 'error'
  | 'new';

/**
 * All processes can be disabled, but are enabled by default
 * A service will accept a simple object for configuration parameters
 */
export type TProcessConfiguration = TResourceConfiguration & {
  disabled: boolean;
};

/**
 * A process is injected in to a process dictionary. The object injected is the
 * process delegate, which by default is the process itself. You can override
 * the `$delegate` method on process to restrict what dependent systems can
 * access.
 */
export interface IProcessDelegate {
  readonly id: string;
}

/**
 * A process manages or monitors a global process, for instance, monitor user activity or
 * performance monitoring, or attaching properties to all HTTP requests.
 *
 * A process is injectable to other process and through the Process Hub it is attached to.
 *
 * Generally a product will register Process to provide all "core" functionality, which all
 * Micro Applications depend on to function properly.
 */
export interface IProcess extends IService {
  /**
   * @example
   *
   * Provide the locator as the key and the name you want to refer to it as as the value
   *
   * ```json
   * ['/authentication']
   * ```
   */
  dependencies: t_locator[];
  /**
   *
   */
  processes: Record<t_locator, IProcessDelegate>;

  /**
   * A Service Hub will provide the configuration for a Service
   */
  configuration: TProcessConfiguration;

  /**
   * A Process is always in a single state, it begins with `new` and once the
   * Process Hub starts or stops the group of process will transition in to
   * a running state.
   *
   * The running state is `activated`.
   */
  state: t_process_state;

  /**
   *
   */
  $memory(m: Object): void;

  /**
   * return a delegate for the service, because exposing the entire service
   * could be dangerous
   */
  $delegate(): IProcessDelegate;
}

/**
 * A Process
 */
export class Process extends Service implements IProcess {
  dependencies = [] as string[];
  processes = {};

  /**
   * the singular configuration associated with this service, defined by the
   * sub-type
   */
  configuration: TProcessConfiguration = {} as TProcessConfiguration;

  /**
   *
   */
  state: t_process_state = 'new';

  /**
   *
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  $memory(m: Object) {}

  /**
   * Every process should return a delegate to be injected in to the process record. Generally
   * we do not want anyone interacting directly with a process because they could call a life-cycle
   * method.
   */
  $delegate() {
    // @note we expect a delegate to proxy the process or just expose the entire service
    // return (this as unknown) as Delegate;
    return (new Proxy(this, {}) as unknown) as IProcessDelegate;
  }
}
